import {
  FC,
  Fragment,
  KeyboardEvent,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Transition } from "@headlessui/react";
import { AppDispatch } from "../../../store";
import { useDispatch, useSelector } from "react-redux";

import {
  closeAcceptPayoutModal,
  payoutSelector,
  setPayout,
} from "../../../store/payout.store.ts";
import { AcceptPayoutResponseType } from "../../types/payment/accept-payout-response.type.ts";
import { useMutation } from "@apollo/client/react";
import { ACCEPT_PAYOUT_MUTATION } from "../../../api/graphql/mutations/accept-payout.mutation.ts";
import { toast } from "react-toastify";
import { NotificationType } from "../../enums/notification.enum.ts";
import NotificationToast from "../toasts/notification.toast.tsx";
import { userSelector } from "../../../store/user.store.ts";
import { formatCurrency } from "../../utils/currency.util.ts";
import { useCookies } from "react-cookie";
import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { getTfaLockCookieValue } from "../../utils/tfa-lock.util.ts";

const AcceptPayoutModal: FC = () => {
  const dispatcher: AppDispatch = useDispatch();
  const [show, setShow] = useState<boolean>(false);
  const {
    acceptModal: { payoutId },
    payout,
  } = useSelector(payoutSelector);
  const [tfaCode, setTfaCode] = useState<string>();
  const [showTfaField, setShowTfaField] = useState<boolean>(false);

  const {
    profile: { isTfaEnabled, isPTfaEnabled, id },
  } = useSelector(userSelector);

  const [acceptPayout, acceptPayoutData] =
    useMutation<AcceptPayoutResponseType>(ACCEPT_PAYOUT_MUTATION);

  const [cookies] = useCookies();
  const tfaLockCookie = getTfaLockCookieValue(cookies, id);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setShowTfaField(
      (isPTfaEnabled || isTfaEnabled) && tfaLockCookie === null,
    );
  }, [isTfaEnabled, isPTfaEnabled, tfaLockCookie]);

  useEffect((): void => {
    if (!payoutId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShow(false);
    } else {
      setShow(true);
    }
  }, [payoutId]);

  useEffect((): void => {
    if (!show) {
      dispatcher(closeAcceptPayoutModal(null));
      dispatcher(setPayout(null));
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTfaCode("");
    }
  }, [dispatcher, show]);

  useEffect(() => {
    if (acceptPayoutData.error) {
      if (CombinedGraphQLErrors.is(acceptPayoutData.error)) {
        const code = acceptPayoutData.error.errors[0]?.extensions?.code;
        if (code === "TFA_CODE_REQUIRED") {
          // eslint-disable-next-line react-hooks/set-state-in-effect
          setShowTfaField(true);
        }
      }

      toast(
        <NotificationToast
          type={NotificationType.WARNING}
          title="Payout"
          message={acceptPayoutData.error.message}
        />,
        {
          toastId: "accept-payout-error",
        },
      );
    }
  }, [acceptPayoutData.error]);

  useEffect(() => {
    if (acceptPayoutData.data) {
      toast(
        <NotificationToast
          type={NotificationType.SUCCESS}
          title="Payout"
          message={acceptPayoutData.data.acceptPayoutOperation.message}
        />,
        {
          toastId: "accept-payout-success",
        },
      );

      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShow(false);
    }
  }, [acceptPayoutData.data]);

  const accept = useCallback(async () => {
    await acceptPayout({
      variables: {
        dto: {
          operationId: payoutId,
        },
        tfaCode,
      },
    });
  }, [acceptPayout, payoutId, tfaCode]);

  const handleTfaSubmit = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== "Enter") {
        return;
      }

      event.preventDefault();

      if (!acceptPayoutData.loading) {
        void accept();
      }
    },
    [accept, acceptPayoutData.loading],
  );

  return (
    <>
      <Transition
        as={Fragment}
        show={show}
        enter="transition ease-out duration-100"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-80"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <div className="fixed inset-0 bg-slate-900/30 z-50 transition-opacity"></div>
      </Transition>
      <Transition
        as={Fragment}
        show={show}
        enter="transition ease-in-out duration-200"
        enterFrom="opacity-0 translate-y-4"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in-out duration-200"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-4"
      >
        <div className="fixed inset-0 z-50 overflow-hidden flex items-center my-4 justify-center transform px-4 sm:px-6">
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-sm shadow-lg overflow-auto max-w-lg w-full max-h-full"
          >
            {/* Modal header */}
            <div className="p-4">
              <div className="text-lg font-semibold text-slate-800">
                Accept Payout Operation
              </div>
            </div>
            <div className="divide-y">
              <div></div>
              <div></div>
            </div>
            {/* Modal content */}
            <div className="p-4 pb-0">
              <div className="text-xs italic mb-6 text-center">
                By submitting this form, you confirm the operation with the
                following parameters:
              </div>

              <div className="p-2">
                {/*<div className="text-sm flex items-center justify-between mr-1 font-medium"><span className="font-bold">Name:</span>{payout?.payoutAddress.name}</div>*/}
                <div className="text-xs flex items-center justify-between mr-1 font-medium">
                  <span className="font-bold text-sm">Address:</span>
                  {payout?.payoutAddress.address}
                </div>
                <div className="text-sm flex items-center justify-between mr-1 font-medium">
                  <span className="font-bold">Amount:</span>
                  {formatCurrency(payout?.amount || "0")} {payout?.currency}
                </div>
              </div>

              <Transition
                as={Fragment}
                show={showTfaField}
                enter="transition ease-in-out duration-600"
                enterFrom="opacity-0 translate-y-4"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in-out duration-300"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-4"
              >
                <div className="mt-2">
                  <label
                    className="block text-sm font-medium mb-1"
                    htmlFor="tfa_code"
                  >
                    2FA Code:
                  </label>
                  <div className="relative">
                    <input
                      name="tfa_code"
                      value={tfaCode}
                      onChange={(e) => setTfaCode(e.target.value)}
                      onKeyDown={handleTfaSubmit}
                      placeholder="2FA Code"
                      className="form-input w-full max-w-161.5 mr-1"
                      type="text"
                    />
                  </div>
                </div>
              </Transition>
            </div>
            {/* Modal footer */}

            <div className="flex justify-end p-4">
              <button
                type="button"
                onClick={() => setShow(false)}
                className="bg-white border text-gray-700 hover:bg-gray-100 hover btn-sm whitespace-nowrap mr-2"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={acceptPayoutData.loading}
                onClick={accept}
                className={`${acceptPayoutData.loading && "opacity-40"} bg-indigo-500 hover:bg-indigo-600 btn-sm text-white whitespace-nowrap`}
              >
                {acceptPayoutData.loading ? (
                  <svg
                    className="animate-spin w-4 h-4 fill-current shrink-0 mr-2"
                    viewBox="0 0 16 16"
                  >
                    <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
                  </svg>
                ) : (
                  ""
                )}
                Accept
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </>
  );
};

export default AcceptPayoutModal;
